import { AnyAction, configureStore, combineReducers } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
import { persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import { diff, atomizeChangeset, applyChangeset } from "json-diff-ts";

import appReducer from "./slices/appSlice";
import bofuReducer from "./slices/bofuSlice";
import bofuSlicePfae from "./slices/bofuSlicePfae";
import registerReducer from "./slices/registerSlice";
import solicitudCreditoReducer from "./slices/solicitudCreditoSlice";
import marketPlaceSlice from "./slices/marketPlaceSlice";
import secondGuaranteePlaceSlice from "./slices/segundoAvalSlice";
import brandSlice from "./slices/brandSlice";

import { baseApi } from "@api/baseApi";
import { setupListeners } from "@reduxjs/toolkit/query";
import { useDispatch } from "react-redux";
import { baseSecondGuaranteApi } from "@api/second_guarantee_api/baseSecondGuaranteeApis";

// Configuración de persistencia
const persistConfig = {
  key: "lenditgroup",
  storage,
  whitelist: ["register", "bofu", "app", "bofuPfae", "marketPalce", "secondGuarante", "theme"],
  blacklist: [baseApi.reducerPath, baseSecondGuaranteApi.reducerPath],
};

// Reducer combinado
const appsReducer = combineReducers({
  register: registerReducer,
  bofu: bofuReducer,
  bofuPfae: bofuSlicePfae,
  app: appReducer,
  solicitudCredito: solicitudCreditoReducer,
  marketPalce: marketPlaceSlice,
  secondGuarante: secondGuaranteePlaceSlice,
  brand: brandSlice,
  [baseApi.reducerPath]: baseApi.reducer,
  [baseSecondGuaranteApi.reducerPath]: baseSecondGuaranteApi.reducer,
});

// Definir RootState para el tipado en el store y en rootReducer
export type RootState = ReturnType<typeof appsReducer>;

const rootReducer = (state: RootState | undefined, action: AnyAction): RootState => {
  if (action.type === "@@INIT") {
    storage
      .getItem("persist:lenditgroup")
      .then((value) => {
        if (state !== undefined && value !== null) {
          const parsedValue = JSON.parse(value);
          Object.keys(parsedValue).forEach((key) => {
            if (key !== "_persist" && key !== undefined) {
              const tmp2 = parsedValue[key];
              if (state) {
                const diffs = diff(tmp2, state[key as keyof RootState]);
                const changes = atomizeChangeset(diffs).filter((change) => change.type === "ADD");
                state[key as keyof RootState] = applyChangeset(tmp2, changes);
              }
            }
          });
          storage.setItem("persist:lenditgroup", JSON.stringify(state));
        }
      })
      .catch((error) => {
        console.error("Error al comparar y actualizar el estado persistente:", error);
      });
  }

  if (action.type === "USER_LOGOUT" || action.type === "CLEAN_STORE") {
    // preserva theme en memoria
    const preservedBrand = state?.brand;

    // resetea todo el store (reducers vuelven a initialState)
    storage.removeItem("persist:lenditgroup");
    state = undefined;

    // reinyecta theme para que no se pierda en el mismo ciclo
    // (appsReducer lo mezclará con initial states del resto)
    return appsReducer(preservedBrand ? ({ brand: preservedBrand } as RootState) : state, {
      type: "@@INIT",
    } as AnyAction);
  }

  return appsReducer(state, action);
};

// Reducer persistente
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configuración del store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(baseApi.middleware)
      .concat(baseSecondGuaranteApi.middleware),
});

// Setup de listeners para RTK Query
setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
